import { Location } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

import { ToastrService } from 'ngx-toastr';

import { TranslateService } from '@ngx-translate/core';

import { doRun, isAssigned, showErrorMessage, showResponseErrorMessage } from '../utils';
import { MetaService, UnknownErrorCallingAPIMessage, GlobalsService } from '../services';
import { OptionsMapDTO, ReadMetaRequestDTO, ResponseDTO } from '../dtos';
import { TranslateBaseComponent } from './translate-base.component';

@Component({ selector: 'app-base-componet', template: '' })
export class BaseComponent extends TranslateBaseComponent {
    private _isLoadingMeta = false;

    @Input()
    loading = false;

    @Output()
    onLoading = new EventEmitter<boolean>();

    @Input()
    metaOptions: OptionsMapDTO = {};

    constructor(
        private readonly _toastr: ToastrService,
        private readonly _globals: GlobalsService,
        private readonly _metaSrv: MetaService,
        private readonly _location: Location,
        translateSrv: TranslateService
    ) {
        super(translateSrv);
    }

    protected emitOnLoading() {
        this.onLoading.emit(this.isLoadingInternal);
    }

    protected toastrError(message: string) {
        showErrorMessage(this._toastr, this.globals, message);
    }

    protected showErrorMessage(response: ResponseDTO, getMessage?: (message?: string) => string): boolean {
        return showResponseErrorMessage(this._toastr, this._globals, response, getMessage);
    }

    protected get toastr(): ToastrService {
        return this._toastr;
    }

    protected get globals(): GlobalsService {
        return this._globals;
    }

    protected get metaSrv(): MetaService {
        return this._metaSrv;
    }

    protected get categoriesToRead(): string[] {
        return [
            'userType,profileStatus,existingContextRoles,scopeTypes,availableContextRoles,aging',
            'booleanOptions,work,contexts,contextRoles,roleStatus,userStatus,approvalStatus,profiles,language',
        ];
    }

    protected get readMetaWithRun(): boolean {
        return false;
    }

    protected readMeta(
        onFinish?: (options: OptionsMapDTO) => void,
        categories?: string[],
        fillRequest: (request: ReadMetaRequestDTO) => void = () => {},
        index = 0,
        options: OptionsMapDTO = {}
    ) {
        const list = categories ?? this.categoriesToRead;
        if (index >= list.length) {
            if (isAssigned(onFinish)) {
                onFinish!(options);
            }
            return;
        }

        const callReadMeta = () => {
            this._isLoadingMeta = true;
            this.emitOnLoading();

            const request: ReadMetaRequestDTO = {
                categories: list[index],
                languageCd: this.translateSrv.currentLang,
            };

            this.fillReadMetaRequest(request);

            fillRequest(request);

            this._metaSrv.readMeta(request).subscribe({
                next: (response) => {
                    this._isLoadingMeta = false;
                    this.emitOnLoading();

                    const metaOptions = { ...options, ...response.meta };

                    this.readMeta(onFinish, categories, fillRequest, index + 1, metaOptions);
                },
                error: (error) => {
                    this._isLoadingMeta = false;
                    this.emitOnLoading();

                    this.toastrError(error.message ?? UnknownErrorCallingAPIMessage);
                },
            });
        };

        if (this.readMetaWithRun) {
            doRun(() => callReadMeta());
        } else {
            callReadMeta();
        }
    }

    protected doReadMeta(options: () => void = () => {}): void {
        this.readMeta((metaOptions) => {
            this.metaOptions = metaOptions;
            options();
        });
    }

    protected fillReadMetaRequest(request: ReadMetaRequestDTO): void {}

    get isLoadingInternal(): boolean {
        return this._isLoadingMeta;
    }

    get isLoading(): boolean {
        return this.loading || this.isLoadingInternal;
    }

    goBack(finish: () => void = () => {}): void {
        doRun(() => {
            this._location.back();
            doRun(() => finish(), 100);
        });
    }
}
